import URITemplate from "urijs/src/URITemplate";

import { MapStringTo, Nullable, NullableResultPromise } from "./base-types";
import { AggregateLoadOption } from "./clients";
import { IExtendedContentHubClient } from "./clients/extended-client";
import { API } from "./constants";
import {
    AGGREGATES,
    ALL_FACETS,
    COPY_ENTITY,
    DATA_SOURCE_BY_NAME,
    DATA_SOURCES,
    ENTITIES,
    ENTITY_BY_ID,
    ENTITY_BY_IDENTIFIER,
    ENTITY_DEFINITION_BY_ID,
    ENTITY_DEFINITION_BY_NAME,
    ENTITY_DEFINITION_BY_NAME_V2,
    ENTITY_DEFINITIONS,
    ENTITY_RELATION_BY_NAME,
    EXPLAIN_USER_PERMISSIONS_FOR_ENTITY,
    PERMISSIONS_FOR_ENTITY,
    RESET_PASSWORD,
    SEARCH,
    SEND_CONFIRMATION_MAIL,
    SEND_NOTIFICATION,
    SET_USER_PASSWORD,
    SETTING,
    UPLOAD,
    UPLOAD_FINALIZE,
} from "./constants/api";
import { ViewMode } from "./contracts/base/view-mode";
import CultureInfo from "./culture-info";
import { ArgumentError } from "./errors/argument-error";
import Guard from "./guard";
import Link from "./link";
import { TypeGuards } from "./type-guards";

export interface ILinkHelper {
    //#region DataSources

    /**
     * Extracts the name of a data source from the specified link.
     * @param link - The link to extract the name from
     * @returns The name as extracted from the link. If the link doesn't contain a name, null is returned.
     */
    nameFromDataSourceAsync(link: Link): NullableResultPromise<string>;

    /**
     * Creates a link to the data source with the specified name.
     * @param name - The name of the data source to link to
     * @returns A link to the data source with the specified name.
     */
    dataSourceToLinkAsync(name: string): Promise<Link>;

    /**
     * Creates a link to the data sources endpoint.
     * @returns A link to the data sources endpoint.
     */
    dataSourcesLinkAsync(): Promise<Readonly<Link>>;

    //#endregion

    //#region Definitions

    /**
     * Extracts the name of an entity definition from the specified link.
     * @param link - The link to extract the name from
     * @returns The name as extracted from the link. If the link doesn't contain a name, null is returned.
     */
    nameFromDefinitionAsync(link: Link): NullableResultPromise<string>;

    /**
     * Extracts the names of the specified entity definition links.
     * @param links - The links to extract the names from
     * @returns The names as extracted from the links. If the link doesn't contain a name, null is returned.
     */
    namesFromDefinitionsAsync(links: Array<Link>): Promise<Array<Nullable<string>>>;

    /**
     * Creates a link to the entity definition with the specified name.
     * @param name - The name of the entity definition to link to
     * @param includeConditionalMembers - [Optional] Include conditional members
     * @param loadPermissions - [Optional] Load permissions for the definitions
     * @returns A link to the entity definition with the specified name.
     */
    definitionToLinkAsync(name: string, includeConditionalMembers?: boolean, loadPermissions?: boolean): Promise<Link>;

    /**
     * Creates a link to the entity definition with the specified id.
     * @param id - The id of the entity definition to link to
     * @param includeConditionalMembers - [Optional] Include conditional members
     * @param loadPermissions - [Optional] Load permissions for the definitions
     * @returns A link to the entity definition with the specified id.
     */
    definitionToLinkAsync(id: number, includeConditionalMembers?: boolean, loadPermissions?: boolean): Promise<Link>;

    definitionToLinkAsync(
        param: string | number,
        includeConditionalMembers?: boolean,
        loadPermissions?: boolean
    ): Promise<Link>;

    /**
     * Creates a link to the entity definition with the specified name.
     * @param name - The name of the entity definition to link to
     * @param includeConditionalMembers - [Optional] Include conditional members
     * @param loadPermissions - [Optional] Load permissions for the definitions
     * @returns A link to the entity definition with the specified name.
     */
    definitionToLinkV2Async(
        name: string,
        includeConditionalMembers?: boolean,
        loadPermissions?: boolean
    ): Promise<Link>;

    /**
     *Creates links to the specified entity definitions by name.
     * @param names - The names of the entity definitions to include in the result
     * @param skip - [Optional] Number of results to skip
     * @param take - [Optional] Number of results to fetch
     * @param includeConditionalMembers - [Optional] Include conditional members
     * @param viewMode - [Optional] Select appropriate ViewMode
     * @param loadPermissions - [Optional] Load permissions for the definitions
     * @param excludeTaxonomyDefinitions - [Optional] Exclude taxonomy definitions from the result
     * @param excludeSystemOwnedDefinitions - [Optional] Exclude system owned definitions from the result
     * @returns Link to the entity definitions with the specified names.
     */
    definitionsToLinkAsync(
        names: Nullable<Array<string>>,
        skip?: number,
        take?: number,
        includeConditionalMembers?: boolean,
        viewMode?: ViewMode,
        loadPermissions?: boolean,
        excludeTaxonomyDefinitions?: boolean,
        excludeSystemOwnedDefinitions?: boolean
    ): Promise<Link>;

    /**
     * Creates links to the specified entity definitions by id.
     * @param ids - The ids of the entity definitions to include in the result
     * @param skip - [Optional] Number of results to skip
     * @param take - [Optional] Number of results to fetch
     * @param includeConditionalMembers - [Optional] Include conditional members
     * @param viewMode - [Optional] Select appropriate ViewMode
     * @param loadPermissions - [Optional] Load permissions for the definitions
     * @param excludeTaxonomyDefinitions - [Optional] Exclude taxonomy definitions from the result
     * @param excludeSystemOwnedDefinitions - [Optional] Exclude system owned definitions from the result
     * @returns Link to the entity definitions with the specified ids.
     */
    definitionsToLinkAsync(
        ids: Nullable<Array<number>>,
        skip?: number,
        take?: number,
        includeConditionalMembers?: boolean,
        viewMode?: ViewMode,
        loadPermissions?: boolean,
        excludeTaxonomyDefinitions?: boolean,
        excludeSystemOwnedDefinitions?: boolean
    ): Promise<Link>;

    /**
     * Creates a link to the entity definitions endpoint.
     * @param skip - [Optional] Number of results to skip
     * @param take - [Optional] Number of results to fetch
     * @param includeConditionalMembers - [Optional] Include conditional members
     * @param viewMode - [Optional] Select appropriate ViewMode
     * @param loadPermissions - [Optional] Load permissions for the definitions
     * @param excludeTaxonomyDefinitions - [Optional] Exclude taxonomy definitions from the result
     * @param excludeSystemOwnedDefinitions - [Optional] Exclude system owned definitions from the result
     * @param filter - [Optional] Filter to apply to the result
     * @param definitionsToLoadByName - [Optional] Comma separated list of entity definition names to load
     * @param definitionsToLoadById - [Optional] Comma separated list of entity definition ids to load
     * @param excludeLightDefinitions - [Optional] Exclude light definitions from the result
     * @returns Link to the entity definitions included in this batch.
     */
    definitionsLinkAsync(
        skip?: number,
        take?: number,
        includeConditionalMembers?: boolean,
        viewMode?: ViewMode,
        loadPermissions?: boolean,
        excludeTaxonomyDefinitions?: boolean,
        excludeSystemOwnedDefinitions?: boolean,
        filter?: string,
        definitionsToLoadByName?: string,
        definitionsToLoadById?: string,
        excludeLightDefinitions?: boolean
    ): Promise<Link>;

    //#endregion

    //#region Entities

    relationToLinkAsync(id: number, name: string): Promise<Link>;

    /**
     * Extracts the id of an entity from the specified link.
     * @param link - The link to extract the name from
     * @returns The id as extracted from the link. If the link doesn't contain an id, null is returned.
     */
    idFromEntityAsync(link: Link): NullableResultPromise<number>;

    /**
     * Extracts the id of entities from the specified links.
     * @param links - The links to extract the names from
     * @returns The ids as extracted from the links.
     */
    idsFromEntitiesAsync(links: Array<Link>): Promise<Array<number>>;

    /**
     * Creates a link to the entity with the specified id.
     * @param id - The id of the entity to link to
     * @param culture - [Optional] Culture for the entity
     * @returns A link to the entity with the specified id.
     */
    entityToLinkAsync(id: number, culture?: Nullable<CultureInfo>): Promise<Link>;

    /**
     * Creates a link to the entity with the specified identifier.
     * @param identifier - The identifier of the entity to link to
     * @returns A link to the entity with the specified identifier.
     */
    entityToLinkAsync(identifier: string): Promise<Link>;

    /**
     * Creates links to the entities with the specified ids.
     * @param ids - The ids of the entities to link to
     * @returns Link to the entities with the specified ids.
     */
    entitiesToLinksAsync(ids: Array<number>): Promise<Array<Link>>;

    /**
     * Extracts the identifier of an entity from the specified
     * @param link - The link to extract the identifier from
     * @returns The identifier as extracted from the link. If the link doesn't contain an identifier, null is returned.
     */
    identifierFromEntityAsync(link: Link): NullableResultPromise<string>;

    /**
     * Creates a link to the entities endpoint.
     * @returns Link to entities endpoint.
     */
    entitiesLinkAsync(): Promise<Readonly<Link>>;

    /**
     * Creates a link to the permissions for entity endpoint.
     * @param id - The entity id
     * @returns Link to the permissions for entity.
     */
    permissionsForEntityToLinkAsync(id: number): Promise<Link>;

    /**
     * Creates a link to the permissions for entity endpoint.
     * @param id - The entity id
     * @param userId - The user id
     * @returns Link to the permissions for entity.
     */
    explainUserPermissionsForEntityToLinkAsync(id: number, userId: number): Promise<Link>;

    /**
     * Creates a link to the copy entities endpoint.
     * @param id - The entity id
     * @returns Link to the copy entities endpoint.
     */
    copyEntityToLinkAsync(id: number): Promise<Link>;

    //#endregion

    //#region Passwords

    /**
     * Gets the link to set the password of the specified user.
     *
     * @param userId - The id of the user
     * @returns Link to set user password.
     */
    setUserPasswordToLinkAsync(userId: number): Promise<Link>;

    /**
     * Gets the link to reset the password of the specified user.
     *
     * @param userId - The id of the user
     * @returns Link to reset user password.
     */
    resetPasswordToLinkAsync(userId: number): Promise<Link>;

    //#endregion

    //#region Policy

    policyToLinkAsync(id: number): Promise<Link>;

    idFromPolicyAsync(link: Link): NullableResultPromise<number>;

    //#endregion

    //#region Commands

    /**
     * Generates a link to a command.
     *
     * @param namespace - The name of the folder containing the command
     * @param command - The name of the command
     * @returns Link for the command.
     */
    commandToLinkAsync(namespace: string, command: string): Promise<Link>;

    //#endregion

    //#region Mails

    sendConfirmationMailToLinkAsync(userId: number): Promise<Link>;

    sendNotificationToLinkAsync(notificationType: string): Promise<Link>;

    //#endregion

    //#region Aggregates

    /**
     * Generate a link to an aggregate resource.
     * @param aggregateName - The name of the aggregate
     * @param entityId - The entity id for which to get the aggregate
     * @param culture - Culture for the entity
     * @param options - Can optionally configure what needs to be loaded from the aggregates (everything will be loaded by default except for permissions)
     * @returns Link to the aggregate resource.
     */
    aggregateToLinkAsync(
        aggregateName: string,
        entityId: number,
        culture?: CultureInfo,
        options?: AggregateLoadOption
    ): Promise<Link>;

    /**
     * Generate a link to a page name.
     * @param pageName - The name or id of the page
     * @param culture - Culture of the page
     * @param entityId - The current entity id (Optional)
     */
    pageResourceToLinkAsync(page: string | number, culture: CultureInfo, entityId?: number): Promise<Link>;

    //#endregion

    //#region Search

    searchLinkAsync(): Promise<Readonly<Link>>;

    allFacetsAsync(): Promise<Readonly<Link>>;

    //#endregion

    //#region Upload

    createUploadToLinkAsync(): Promise<Readonly<Link>>;

    finalizeUploadToLinkAsync(): Promise<Readonly<Link>>;

    //#endregion

    //#region Setting

    settingByNameLink(categoryName: string, settingName: string): Promise<Readonly<Link>>;

    //#endregion
}

export class LinkHelper implements ILinkHelper {
    private readonly _client: IExtendedContentHubClient;

    constructor(client: IExtendedContentHubClient) {
        Guard.notNullOrUndefined(client);
        this._client = client;
    }

    //#region DataSources
    async nameFromDataSourceAsync(link: Link): NullableResultPromise<string> {
        if (link == null) return null;

        const variable = await this.getVariableValueAsync(
            link,
            DATA_SOURCE_BY_NAME.templateName,
            DATA_SOURCE_BY_NAME.name
        );

        return variable || null;
    }

    async dataSourceToLinkAsync(name: string): Promise<Link> {
        Guard.stringNotNullOrEmpty(name);

        return await this.bindAsync(DATA_SOURCE_BY_NAME.templateName, { [DATA_SOURCE_BY_NAME.name]: name });
    }

    async dataSourcesLinkAsync(): Promise<Readonly<Link>> {
        const routes = await this._client.api.getApiRoutesAsync();
        return routes[DATA_SOURCES.templateName];
    }

    //#endregion

    //#region Definitions
    async nameFromDefinitionAsync(link: Link): NullableResultPromise<string> {
        if (!link) return null;

        const variable = await this.getVariableValueAsync(
            link,
            ENTITY_DEFINITION_BY_NAME.templateName,
            ENTITY_DEFINITION_BY_NAME.name
        );

        return variable || null;
    }

    async namesFromDefinitionsAsync(links: Array<Link>): Promise<Array<Nullable<string>>> {
        if (!links) return [];

        const result: Array<Nullable<string>> = [];

        await Promise.all(
            links.map(async link => {
                result.push(await this.nameFromDefinitionAsync(link));
            })
        );

        return result;
    }

    async definitionToLinkAsync(
        param: string | number,
        includeConditionalMembers?: boolean,
        loadPermissions?: boolean
    ): Promise<Link> {
        Guard.notNullOrUndefined(param);
        if (typeof param === "string") {
            Guard.stringNotNullOrEmpty(param);

            const variables: MapStringTo<string> = {
                [ENTITY_DEFINITION_BY_NAME.name]: param,
            };

            if (includeConditionalMembers != null) {
                variables[ENTITY_DEFINITION_BY_NAME.includeConditionalMembers] = includeConditionalMembers.toString();
            }
            if (loadPermissions != null) {
                variables[ENTITY_DEFINITION_BY_NAME.loadPermissions] = loadPermissions.toString();
            }

            return await this.bindAsync(ENTITY_DEFINITION_BY_NAME.templateName, variables);
        } else {
            Guard.validId(param);

            const variables: MapStringTo<string> = {
                [ENTITY_DEFINITION_BY_ID.id]: param.toString(),
            };

            if (includeConditionalMembers != null) {
                variables[ENTITY_DEFINITION_BY_ID.includeConditionalMembers] = includeConditionalMembers.toString();
            }
            if (loadPermissions != null) {
                variables[ENTITY_DEFINITION_BY_ID.loadPermissions] = loadPermissions.toString();
            }

            return await this.bindAsync(ENTITY_DEFINITION_BY_ID.templateName, variables);
        }
    }

    async definitionToLinkV2Async(
        name: string,
        includeConditionalMembers?: boolean,
        loadPermissions?: boolean
    ): Promise<Link> {
        Guard.stringNotNullOrEmpty(name);

        const variables: MapStringTo<string> = {
            [ENTITY_DEFINITION_BY_NAME_V2.name]: name,
        };

        if (includeConditionalMembers != null) {
            variables[ENTITY_DEFINITION_BY_NAME_V2.includeConditionalMembers] = includeConditionalMembers.toString();
        }

        if (loadPermissions != null) {
            variables[ENTITY_DEFINITION_BY_NAME_V2.loadPermissions] = loadPermissions.toString();
        }

        return await this.bindAsync(ENTITY_DEFINITION_BY_NAME_V2.templateName, variables);
    }

    async definitionsToLinkAsync(
        values: Nullable<Array<string>> | Nullable<Array<number>>,
        skip?: number,
        take?: number,
        includeConditionalMembers?: boolean,
        viewMode?: ViewMode,
        loadPermissions?: boolean,
        excludeTaxonomyDefinitions?: boolean,
        excludeSystemOwnedDefinitions?: boolean
    ): Promise<Link> {
        const variables = LinkHelper.applyEntityDefinitionVariables({
            skip,
            take,
            includeConditionalMembers,
            viewMode,
            loadPermissions,
            excludeTaxonomyDefinitions,
            excludeSystemOwnedDefinitions,
        });

        if (values == null && arguments.length === 1) {
            throw new ArgumentError("Missing function arguments.");
        }

        if (values) {
            Guard.arrayNotEmpty(values);
            if (TypeGuards.isStringArray(values)) {
                variables[ENTITY_DEFINITIONS.definitionsToLoadByName] = values.join();
            } else {
                variables[ENTITY_DEFINITIONS.definitionsToLoadById] = values.join();
            }
        }

        return await this.bindAsync(ENTITY_DEFINITIONS.templateName, variables);
    }

    async definitionsLinkAsync(
        skip?: number,
        take?: number,
        includeConditionalMembers?: boolean,
        viewMode?: ViewMode,
        loadPermissions?: boolean,
        excludeTaxonomyDefinitions?: boolean,
        excludeSystemOwnedDefinitions?: boolean,
        filter?: string,
        definitionsToLoadByName?: string,
        definitionsToLoadById?: string,
        excludeLightDefinitions?: boolean
    ): Promise<Link> {
        const variables = LinkHelper.applyEntityDefinitionVariables({
            skip,
            take,
            includeConditionalMembers,
            viewMode,
            loadPermissions,
            excludeTaxonomyDefinitions,
            excludeSystemOwnedDefinitions,
            filter,
            definitionsToLoadByName,
            definitionsToLoadById,
            excludeLightDefinitions,
        });

        return await this.bindAsync(ENTITY_DEFINITIONS.templateName, variables);
    }

    //#endregion

    //#region Entities
    async idFromEntityAsync(link: Link): Promise<number | null> {
        if (!link) return null;

        const variable = await this.getVariableValueAsync(link, ENTITY_BY_ID.templateName, ENTITY_BY_ID.id);

        if (!variable) return null;

        Guard.parsableInteger(variable);

        return parseInt(variable);
    }

    async idsFromEntitiesAsync(links: Array<Link>): Promise<Array<number>> {
        if (!links || links.length === 0) return [];

        const list: Array<number> = [];
        for (const link of links) {
            const id = await this.idFromEntityAsync(link);
            if (id != null && id !== 0) {
                list.push(id);
            }
        }

        return list;
    }

    async entityToLinkAsync(param: number | string, culture?: Nullable<CultureInfo>): Promise<Link> {
        Guard.notNullOrUndefined(param);
        if (typeof param === "string") {
            Guard.stringNotNullOrEmpty(param);
            return await this.bindAsync(ENTITY_BY_IDENTIFIER.templateName, {
                [ENTITY_BY_IDENTIFIER.identifier]: param,
            });
        } else {
            const variables: Record<string, string> = {
                [ENTITY_BY_ID.id]: param.toString(),
            };
            if (culture != null) {
                variables[ENTITY_BY_ID.culture] = culture;
            }
            return await this.bindAsync(ENTITY_BY_ID.templateName, variables);
        }
    }

    async relationToLinkAsync(id: number, name: string): Promise<Link> {
        Guard.notNullOrUndefined(id);
        const variables: Record<string, string> = {
            [ENTITY_RELATION_BY_NAME.id]: id.toString(),
        };
        if (name != null) {
            variables[ENTITY_RELATION_BY_NAME.name] = name;
        }
        return await this.bindAsync(ENTITY_RELATION_BY_NAME.templateName, variables);
    }

    async entitiesToLinksAsync(ids: Array<number>): Promise<Array<Link>> {
        if (!ids || ids.length === 0) return [];

        Guard.validIds(ids);

        const list: Array<Link> = [];
        for (const id of ids) {
            const link = await this.entityToLinkAsync(id);
            if (link) {
                list.push(link);
            }
        }

        return list;
    }

    async identifierFromEntityAsync(link: Link): NullableResultPromise<string> {
        if (!link) return null;

        return await this.getVariableValueAsync(
            link,
            ENTITY_BY_IDENTIFIER.templateName,
            ENTITY_BY_IDENTIFIER.identifier
        );
    }

    async entitiesLinkAsync(): Promise<Readonly<Link>> {
        const routes = await this._client.api.getApiRoutesAsync();
        return routes[ENTITIES.templateName];
    }

    async permissionsForEntityToLinkAsync(id: number): Promise<Link> {
        Guard.validId(id);

        return await this.bindAsync(PERMISSIONS_FOR_ENTITY.templateName, PERMISSIONS_FOR_ENTITY.id, id.toString());
    }

    async explainUserPermissionsForEntityToLinkAsync(id: number, userId: number): Promise<Link> {
        Guard.validId(id);
        Guard.validId(userId);

        const variables = {
            [EXPLAIN_USER_PERMISSIONS_FOR_ENTITY.id]: id.toString(),
            [EXPLAIN_USER_PERMISSIONS_FOR_ENTITY.userId]: userId.toString(),
        };
        return await this.bindAsync(EXPLAIN_USER_PERMISSIONS_FOR_ENTITY.templateName, variables);
    }

    async copyEntityToLinkAsync(id: number): Promise<Link> {
        Guard.validId(id);

        return await this.bindAsync(COPY_ENTITY.templateName, COPY_ENTITY.id, id.toString());
    }

    //#endregion

    //#region Passwords

    async setUserPasswordToLinkAsync(userId: number): Promise<Link> {
        Guard.validId(userId);

        return await this.bindAsync(SET_USER_PASSWORD.templateName, SET_USER_PASSWORD.userId, userId.toString());
    }

    async resetPasswordToLinkAsync(userId: number): Promise<Link> {
        Guard.validId(userId);

        return await this.bindAsync(RESET_PASSWORD.templateName, RESET_PASSWORD.userId, userId.toString());
    }

    //#endregion

    //#region Policy
    async policyToLinkAsync(id: number): Promise<Link> {
        Guard.validId(id);

        return await this.bindAsync(API.POLICY_BY_ID.templateName, API.POLICY_BY_ID.id, id.toString());
    }

    async idFromPolicyAsync(link: Link): NullableResultPromise<number> {
        if (link == null) {
            return null;
        }

        const variable = await this.getVariableValueAsync(link, API.POLICY_BY_ID.templateName, API.POLICY_BY_ID.id);

        Guard.parsableInteger(variable);

        return parseInt(variable);
    }

    //#endregion

    //#region Commands
    async commandToLinkAsync(namespace: string, command: string): Promise<Link> {
        Guard.stringNotNullOrEmpty(namespace);
        Guard.stringNotNullOrEmpty(command);

        const variables: MapStringTo<string> = {
            [API.COMMANDS.folder]: namespace,
            [API.COMMANDS.command]: command,
        };

        const res = await this.bindAsync(API.COMMANDS.templateName, variables);

        return res;
    }

    //#endregion

    //#region Mails
    async sendConfirmationMailToLinkAsync(userId: number): Promise<Link> {
        Guard.validId(userId);

        return await this.bindAsync(
            SEND_CONFIRMATION_MAIL.templateName,
            SEND_CONFIRMATION_MAIL.userId,
            userId.toString()
        );
    }

    async sendNotificationToLinkAsync(notificationType: string): Promise<Link> {
        Guard.stringNotNullOrEmpty(notificationType);

        const task = await this.bindAsync(SEND_NOTIFICATION.templateName, SEND_NOTIFICATION.type, notificationType);

        return task;
    }

    //#endregion

    //#region Aggregates
    async aggregateToLinkAsync(
        aggregateName: string,
        entityId: number,
        culture?: CultureInfo,
        {
            loadPermissions,
            loadOptionLists = true,
            loadSettings = true,
            loadEntities = true,
            loadDefinitions = true,
        }: AggregateLoadOption = {}
    ): Promise<Link> {
        Guard.stringNotNullOrEmpty(aggregateName);
        Guard.validId(entityId);

        const variables: MapStringTo<string> = {
            [API.AGGREGATES.name]: aggregateName,
            [API.AGGREGATES.id]: entityId.toString(),
            [API.AGGREGATES.loadOptionLists]: loadOptionLists.toString(),
            [API.AGGREGATES.loadSettings]: loadSettings.toString(),
            [API.AGGREGATES.loadDefinitions]: loadDefinitions.toString(),
            [API.AGGREGATES.loadEntities]: loadEntities.toString(),
        };

        if (culture) {
            variables[API.AGGREGATES.culture] = culture;
        }

        if (loadPermissions != undefined) {
            variables[API.AGGREGATES.loadPermissions] = loadPermissions.toString();
        }

        const aggregateLink = await this.bindAsync(AGGREGATES.templateName, variables);

        return aggregateLink;
    }

    //#endregion

    //#region PageResource
    async pageResourceToLinkAsync(page: string | number, culture: CultureInfo, entityId?: number): Promise<Link> {
        Guard.stringNotNullOrEmpty(culture);
        Guard.notNullOrUndefined(page);

        const variables: MapStringTo<string> = {
            [API.PAGE_BY_IDENTIFIER.culture]: culture,
        };

        if (entityId) {
            variables[API.PAGE_BY_IDENTIFIER.entityId] = entityId.toString();
        }

        let templateName = "";

        if (typeof page === "string") {
            Guard.stringNotNullOrEmpty(page);

            variables[API.PAGE_BY_IDENTIFIER.identifier] = page;

            templateName = API.PAGE_BY_IDENTIFIER.templateName;
        } else {
            Guard.validId(page);

            variables[API.PAGE_BY_ID.id] = page.toString();

            templateName = API.PAGE_BY_ID.templateName;
        }

        const pageLink = await this.bindAsync(templateName, variables);

        return pageLink;
    }

    //#endregion

    //#region Search
    async searchLinkAsync(): Promise<Readonly<Link>> {
        const routes = await this._client.api.getApiRoutesAsync();
        return routes[SEARCH.templateName];
    }

    async allFacetsAsync(): Promise<Readonly<Link>> {
        const routes = await this._client.api.getApiRoutesAsync();
        return routes[ALL_FACETS.templateName];
    }

    //#endregion

    // #region Search
    async settingByNameLink(categoryName: string, settingName: string): Promise<Readonly<Link>> {
        return await this.bindAsync(SETTING.templateName, {
            [SETTING.category]: categoryName,
            [SETTING.name]: settingName,
        });
    }

    //#endregion

    //#region Upload
    async createUploadToLinkAsync(): Promise<Readonly<Link>> {
        const routes = await this._client.api.getApiRoutesAsync();
        return routes[UPLOAD.templateName];
    }

    async finalizeUploadToLinkAsync(): Promise<Readonly<Link>> {
        const routes = await this._client.api.getApiRoutesAsync();
        return routes[UPLOAD_FINALIZE.templateName];
    }

    //#endregion

    private async getVariableValueAsync(link: Link, templateName: string, variable: string): Promise<string> {
        const routes = await this._client.api.getApiRoutesAsync();
        const value = link.getVariableValue(routes[templateName], variable);
        return value;
    }

    private async bindAsync(
        templateName: string,
        value: string | MapStringTo<string>,
        argument?: string
    ): Promise<Link> {
        let values: MapStringTo<string> = {};
        if (typeof value === "string") {
            Guard.stringNotNullOrEmpty(value);
            Guard.notNullOrUndefined(argument);

            values[value] = argument!;
        } else {
            values = value;
        }

        const routes = await this._client.api.getApiRoutesAsync();
        const link = routes[templateName];

        if (!link.templated) {
            return new Link(link.href);
        } else {
            const template = new URITemplate(link.href);
            return new Link(template.expand(values));
        }
    }

    private static applyEntityDefinitionVariables({
        skip,
        take,
        filter,
        definitionsToLoad,
        definitionsToLoadByName,
        definitionsToLoadById,
        viewMode,
        loadPermissions,
        includeConditionalMembers,
        excludeTaxonomyDefinitions,
        excludeSystemOwnedDefinitions,
        excludeLightDefinitions,
    }: {
        skip?: number;
        take?: number;
        filter?: string;
        definitionsToLoad?: string;
        definitionsToLoadByName?: string;
        definitionsToLoadById?: string;
        viewMode?: ViewMode;
        loadPermissions?: boolean;
        includeConditionalMembers?: boolean;
        excludeTaxonomyDefinitions?: boolean;
        excludeSystemOwnedDefinitions?: boolean;
        excludeLightDefinitions?: boolean;
    }): MapStringTo<string> {
        const variables: MapStringTo<string> = {};

        if (skip != null && skip !== 0) {
            variables[ENTITY_DEFINITIONS.skip] = skip.toString();
        }
        if (take != null) {
            variables[ENTITY_DEFINITIONS.take] = take.toString();
        }
        if (includeConditionalMembers != null) {
            variables[ENTITY_DEFINITIONS.includeConditionalMembers] = includeConditionalMembers.toString();
        }
        if (viewMode != null) {
            variables[ENTITY_DEFINITIONS.viewMode] = viewMode;
        }
        if (loadPermissions != null) {
            variables[ENTITY_DEFINITIONS.loadPermissions] = loadPermissions.toString();
        }
        if (filter != null) {
            variables[ENTITY_DEFINITIONS.filter] = filter;
        }
        if (definitionsToLoad != null) {
            variables[ENTITY_DEFINITIONS.definitionsToLoad] = definitionsToLoad.toString();
        }
        if (definitionsToLoadByName != null) {
            variables[ENTITY_DEFINITIONS.definitionsToLoadByName] = definitionsToLoadByName.toString();
        }
        if (definitionsToLoadById != null) {
            variables[ENTITY_DEFINITIONS.definitionsToLoadById] = definitionsToLoadById.toString();
        }
        if (excludeTaxonomyDefinitions != null) {
            variables[ENTITY_DEFINITIONS.excludeTaxonomyDefinitions] = excludeTaxonomyDefinitions.toString();
        }
        if (excludeSystemOwnedDefinitions != null) {
            variables[ENTITY_DEFINITIONS.excludeSystemOwnedDefinitions] = excludeSystemOwnedDefinitions.toString();
        }
        if (excludeLightDefinitions != null) {
            variables[ENTITY_DEFINITIONS.excludeLightDefinitions] = excludeLightDefinitions.toString();
        }

        return variables;
    }
}
